import appStore from "@/store/modules/appStore";

export type AcceptedImageTypes = (typeof acceptedImageTypes)[number];

export interface DownscaleImageConfig {
  newWidthPixels: number;
  imageType: AcceptedImageTypes;
  quality: number;
}

export type CameraCaptureMode = "user" | "environment";

export const acceptedImageTypes = ["image/jpeg", "image/png"];

/**
 * Downscale an image to a new width in pixels and a lower quality.
 */
export const downscaleBase64Image = async (
  base64Image: string,
  downscaleImageConfig: DownscaleImageConfig = {
    newWidthPixels: 800,
    imageType: "image/jpeg",
    quality: 0.5,
  }
) => {
  const { newWidthPixels, imageType, quality } = downscaleImageConfig;

  // Create a temporary image so that we can compute the height of the downscaled image.
  const image = new Image();
  image.src = base64ImageToDataUrl(base64Image, downscaleImageConfig.imageType);
  await image.decode();
  // wait till the image loads

  const oldWidth = image.width;
  const oldHeight = image.height;
  const newHeight = Math.floor((oldHeight / oldWidth) * newWidthPixels);

  // Create a temporary canvas to draw the downscaled image on.
  const canvas = document.createElement("canvas");
  canvas.width = newWidthPixels;
  canvas.height = newHeight;

  // Draw the downscaled image on the canvas and return the new image
  const ctx = canvas.getContext("2d");
  ctx?.drawImage(image, 0, 0, newWidthPixels, newHeight);
  return stripImagePrefixFromBase64DataUrl(
    canvas.toDataURL(imageType, quality)
  );
};

/**
 * @desc initiates download of a file from a base64 data URL
 * @param {string} base64ImageDataUrl  dataUrl of base64 image
 * @param {string} fileExtension with prefixed dot (.png | .jpg | .jpeg)
 * @param {string} fileName Name without extensions or dots
 */
export const initiateDownloadOfBase64DataUrl = (
  base64ImageDataUrl: string,
  fileExtension: string = ".jpeg",
  fileName: string = "Image"
) => {
  appStore.pushSnackbarConfig({ text: "Downloading image" });
  const a = document.createElement("a");
  a.href = base64ImageDataUrl;
  a.download = fileName + fileExtension;
  a.click(); //Download file
};

/**
 * @desc initiates download of a file from a base64 string
 * @param {string} base64Image Base64 image
 * @param {string} fileExtension with prefixed dot (.png | .jpg | .jpeg)
 * @param {string} fileName Name without extensions or dots
 * @param {AcceptedImageTypes} imageType eg. image/jpeg (default)
 */
export const initiateDownloadOfBase64Image = (
  base64Image: string,
  fileExtension: string = ".jpeg",
  fileName: string = "Image",
  imageType: AcceptedImageTypes = "image/jpeg"
) => {
  initiateDownloadOfBase64DataUrl(
    base64ImageToDataUrl(base64Image, imageType),
    fileExtension,
    fileName
  );
};

/**
 *
 * @desc Converts base64 image to dataUrl by adding image type prefix
 * @param base64Image to be converted to dataUrl
 * @returns {string} base64 Image as dataUrl
 */
export const base64ImageToDataUrl = (
  base64Image: string,
  imageType: AcceptedImageTypes = "image/jpeg"
) => {
  return `data:${imageType};base64,${base64Image}`;
};

/**
 *
 * @param dataUrl Data url with ImageStringPrefix (eg data:image/jpeg;base64,)
 * @returns dataUrl without ImageStringPrefix
 */
export const stripImagePrefixFromBase64DataUrl = (dataUrl: string) => {
  /* eslint-disable no-useless-escape */
  return dataUrl.replace(/^data:([\w\/\+]+);(charset=[\w-]+|base64).*,/, "");
};
